import React, { useCallback } from 'react'
import i18next from 'i18next'
import { UsersIcon } from '@heroicons/react/24/outline'
import { API, IPerson } from '../../services/api'
import { Autocomplete } from '../Autocomplete'
import { useDebouncedOptions } from '../../utils/useDebouncedOptions'

interface PersonSearchSelectProps {
  workspaceId: string
  excludeIds: Array<string>
  onSelect: (person: IPerson) => void
}

/** Server-side person search (debounced, few results) — the list is never fetched whole. */
export const PersonSearchSelect: React.FC<PersonSearchSelectProps> = ({
  workspaceId,
  excludeIds,
  onSelect,
}) => {
  const search = useCallback(
    async (phrase: string): Promise<Array<IPerson>> => {
      const response = await API.getInstance().getPersons({ workspaceId, phrase, count: 5 })
      return response.ok && response.data ? response.data : []
    },
    [workspaceId]
  )
  const { query, setQuery, options, loading } = useDebouncedOptions<IPerson>({ search })

  const visible = options.filter((person) => !excludeIds.includes(person.id))

  return (
    <Autocomplete
      // remount clears the typed query after each multi-select pick
      key={excludeIds.length}
      options={visible.map((person) => ({
        id: person.id,
        label: person.name,
        avatarUrl: person.cover_url ?? undefined,
      }))}
      onChange={(option) => {
        const person = visible.find((p) => p.id === option?.id)
        if (person) onSelect(person)
      }}
      onQueryChange={setQuery}
      loading={loading}
      placeholder={i18next.t('filters.personsPlaceholder')}
      icon={<UsersIcon className="h-4 w-4 text-gray-400" />}
      emptyMessage={
        query.trim().length < 2 ? i18next.t('filters.typeToSearch') : i18next.t('filters.noResults')
      }
    />
  )
}
